
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, User, Banknote, Car, Calculator as CalculatorIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ApplyNowModal } from "@/components/common/ApplyNowModal";

export const ServicesGrid = () => {
  const navigate = useNavigate();

  const handleCalculateEMI = (loanType: string) => {
    // Navigate to calculators page with the specific loan type
    navigate('/calculators', { state: { activeCalculator: `${loanType} EMI` } });
  };
  const services = [
    {
      icon: Home,
      title: "Home Loan",
      description: "Buy your dream home with lowest interest rates",
      minAmount: "₹1,00,000",
      maxAmount: "₹5,00,00,000",
      rate: "8.40%",
      features: ["Up to 90% Funding", "30 Year Tenure", "Tax Benefits"]
    },
    {
      icon: Banknote,
      title: "Business Loan",
      description: "Grow your business with working capital",
      minAmount: "₹50,000",
      maxAmount: "₹50,00,000",
      rate: "12.00%",
      features: ["Quick Processing", "Minimal Documentation", "Flexible Repayment"]
    },
    {
      icon: Home, // Using Home icon as placeholder for Mortgage
      title: "Mortgage Loan",
      description: "Leverage your property for financial needs",
      minAmount: "₹5,00,000",
      maxAmount: "₹10,00,00,000",
      rate: "9.50%",
      features: ["High Loan Amount", "Long Tenure", "Low Interest Rates"]
    },
    {
      icon: Banknote, // Using Banknote icon as placeholder for Project
      title: "Project Loan",
      description: "Finance your business projects and expansions",
      minAmount: "₹10,00,000",
      maxAmount: "₹25,00,00,000",
      rate: "11.50%",
      features: ["Customized Solutions", "Flexible Repayment", "End-to-end Support"]
    },
    {
      icon: User,
      title: "Personal Loan",
      description: "Instant personal loans up to ₹20 Lakhs",
      minAmount: "₹10,000",
      maxAmount: "₹20,00,000",
      rate: "10.49%",
      features: ["No Collateral", "Instant Disbursal", "Flexible Tenure"]
    },
    {
      icon: CalculatorIcon,
      title: "Education Loan",
      description: "Invest in your future with education loans",
      minAmount: "₹50,000",
      maxAmount: "₹1,50,00,000",
      rate: "9.15%",
      features: ["No Collateral up to 7.5L", "Moratorium Period", "Tax Benefits"]
    },
    {
      icon: Car,
      title: "New Car Loan",
      description: "Drive your dream car today",
      minAmount: "₹1,00,000",
      maxAmount: "₹1,00,00,000",
      rate: "8.70%",
      features: ["Up to 90% Funding", "7 Year Tenure", "Fast Approval"]
    },
    {
      icon: Car,
      title: "Used Car Loan",
      description: "Pre-owned cars with attractive financing",
      minAmount: "₹1,00,000",
      maxAmount: "₹50,00,000",
      rate: "9.50%",
      features: ["Up to 80% Funding", "5 Year Tenure", "Quick Processing"]
    }
  ];

  return (
    <section className="py-20 px-4">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Loan Products
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Choose from our wide range of financial products designed for your needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full w-fit">
                  <service.icon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white">{service.title}</CardTitle>
                <CardDescription className="text-gray-300">
                  {service.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center p-2 bg-blue-500/20 rounded-lg">
                    <div className="text-blue-400 font-semibold">Min Amount</div>
                    <div className="text-white">{service.minAmount}</div>
                  </div>
                  <div className="text-center p-2 bg-purple-500/20 rounded-lg">
                    <div className="text-purple-400 font-semibold">Max Amount</div>
                    <div className="text-white">{service.maxAmount}</div>
                  </div>
                </div>

                <div className="text-center p-3 bg-green-500/20 rounded-lg">
                  <div className="text-green-400 font-semibold">Interest Rate from</div>
                  <div className="text-2xl font-bold text-white">{service.rate}* p.a.</div>
                </div>

                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-gray-300">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2 pt-4">
                  <ApplyNowModal loanType={service.title}>
                    <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      Apply Now
                    </Button>
                  </ApplyNowModal>
                  <Button 
                    variant="outline" 
                    className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => handleCalculateEMI(service.title)}
                  >
                    Calculate EMI
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
